import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import PageBanner from '../components/PageBanner';
import { FaHome, FaUtensils, FaUsers, FaHeart, FaGlobe, FaStar } from 'react-icons/fa';

export default function AboutUs() {
  const { t } = useTranslation();
  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-900 font-normal mb-6">
            <Link to="/" className="flex items-center hover:text-green-600 transition-colors">
              <FaHome className="w-4 h-4 mr-1" />
              {t('about.home')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{t('about.breadcrumb')}</span>
          </nav>
        </div>

        <PageBanner />

        {/* Header Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-900 font-normal max-w-3xl mx-auto">
              {t('about.intro')}
            </p>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto">
            {/* Mission Section */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaHeart className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('about.missionTitle')}</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t('about.missionText')}
              </p>
            </section>

            {/* Platform Description */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaUtensils className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('about.platformTitle')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">
                  <strong>{t('about.platformTagline')}</strong>
                </p>
                <p className="leading-relaxed">
                  {t('about.platformText1')}
                </p>
                <p className="leading-relaxed">
                  {t('about.platformText2')}
                </p>
              </div>
            </section>

            {/* Experience Section */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaStar className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('about.experienceTitle')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">
                  {t('about.experienceText1')}
                </p>
                <p className="leading-relaxed">
                  {t('about.experienceText2')}
                </p>
              </div>
            </section>

            {/* Partners Section */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaUsers className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('about.partnersTitle')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">
                  {t('about.partnersText1')}
                </p>
                <p className="leading-relaxed">
                  {t('about.partnersText2')}
                </p>
              </div>
            </section>

            {/* Trust Section */}
            <section className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
                  <FaGlobe className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('about.trustTitle')}</h2>
              </div>
              <div className="space-y-6 text-gray-700">
                <p className="leading-relaxed">
                  {t('about.trustText1')}
                </p>
                <p className="leading-relaxed">
                  {t('about.trustText2')}
                </p>
              </div>
            </section>

            {/* Values Section */}
            <section className="bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('about.valuesTitle')}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('about.authenticityTitle')}</h3>
                  <p className="text-gray-700">
                    {t('about.authenticityText')}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('about.qualityTitle')}</h3>
                  <p className="text-gray-700">
                    {t('about.qualityText')}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('about.communityTitle')}</h3>
                  <p className="text-gray-700">
                    {t('about.communityText')}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('about.innovationTitle')}</h3>
                  <p className="text-gray-700">
                    {t('about.innovationText')}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
